"use client";

import * as React from "react";
import {
  Content as RadixContent,
  Item as RadixItem,
  CheckboxItem as RadixCheckboxItem,
  RadioItem as RadixRadioItem,
  type ContentProps as RadixContentProps,
  type ItemProps as RadixItemProps,
  type CheckboxItemProps as RadixCheckboxItemProps,
  type RadioItemProps as RadixRadioItemProps,
} from "@radix-ui/themes/components/dropdown-menu";
import cx from "clsx";
import { useElement } from "../widgets-context.js";
import { getDomProps, namespaceClassNames } from "../utils.js";

interface ContentProps extends Omit<RadixContentProps, "variant"> {}

const DropdownMenuContent = React.forwardRef<HTMLDivElement, ContentProps>(
  function DropdownMenuContent({ className, ...props }, ref) {
    const element = useElement("dropdown");
    return (
      <RadixContent
        ref={ref}
        className={cx(namespaceClassNames("dropdown"), className)}
        {...props}
        {...element}
      />
    );
  },
);

interface ItemProps extends Omit<RadixItemProps, "variant" | "color"> {
  variant?: "primary" | "destructive";
}

const MenuItem = React.forwardRef<HTMLDivElement, ItemProps>(function MenuItem(
  { className, variant = "primary", ...props },
  ref,
) {
  const element = useElement(`${variant}MenuItem`);
  return (
    <RadixItem
      ref={ref}
      {...getDomProps({
        elementId: `${variant}MenuItem`,
        className: cx(
          className,
          // TODO: Remove BEM-style selector once conventions are standardized
          variant !== "primary" && `menu-item--${variant}`,
        ),
      })}
      {...getItemRadixProps(variant)}
      {...props}
      {...element}
    />
  );
});

interface CheckboxItemProps extends Omit<RadixCheckboxItemProps, "variant"> {
  variant?: "primary";
}

const CheckboxMenuItem = React.forwardRef<HTMLDivElement, CheckboxItemProps>(
  function CheckboxMenuItem({ className, variant = "primary", ...props }, ref) {
    const element = useElement(`${variant}MenuItem`);
    return (
      <RadixCheckboxItem
        ref={ref}
        className={cx(
          className,
          namespaceClassNames(
            "menu-item",
            // TODO: Remove BEM-style selector once conventions are standardized
            variant !== "primary" && `menu-item--${variant}`,
          ),
        )}
        {...getItemRadixProps(variant)}
        {...props}
        {...element}
      />
    );
  },
);

interface RadioItemProps extends Omit<RadixRadioItemProps, "variant"> {
  variant?: "primary";
}

const RadioMenuItem = React.forwardRef<HTMLDivElement, RadioItemProps>(
  function RadioMenuItem({ className, variant = "primary", ...props }, ref) {
    const element = useElement(`${variant}MenuItem`);
    return (
      <RadixRadioItem
        ref={ref}
        className={cx(
          className,
          namespaceClassNames(
            "menu-item",
            // TODO: Remove BEM-style selector once conventions are standardized
            variant !== "primary" && `menu-item--${variant}`,
          ),
        )}
        {...getItemRadixProps(variant)}
        {...props}
        {...element}
      />
    );
  },
);

function getItemRadixProps(
  variant: Exclude<ItemProps["variant"], undefined>,
): Pick<RadixItemProps, "color"> {
  if (variant === "primary") {
    return {};
  }
  if (variant === "destructive") {
    return {
      color: "red",
    };
  }
  variant satisfies never;
  return {};
}

export {
  DropdownMenuContent as Content,
  MenuItem as Item,
  CheckboxMenuItem as CheckboxItem,
  RadioMenuItem as RadioItem,
};

export type { ContentProps, ItemProps, CheckboxItemProps, RadioItemProps };

export {
  Group,
  Label,
  RadioGroup,
  Root,
  Separator,
  Sub,
  SubContent,
  SubTrigger,
  Trigger,
  TriggerIcon,
} from "@radix-ui/themes/components/dropdown-menu";
export type {
  GroupProps,
  LabelProps,
  RadioGroupProps,
  RootProps,
  SeparatorProps,
  SubContentProps,
  SubProps,
  SubTriggerProps,
  TriggerIconProps,
  TriggerProps,
} from "@radix-ui/themes/components/dropdown-menu";
